import $api from "@http";

const appendNestedFormData = (formData, prefix, value) => {
  if (value === null || value === undefined) {
    return;
  }

  if (value instanceof File) {
    formData.append(prefix, value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendNestedFormData(formData, `${prefix}[${index}]`, item);
    });
    return;
  }

  if (typeof value === "object") {
    Object.entries(value).forEach(([key, nestedValue]) => {
      appendNestedFormData(formData, `${prefix}[${key}]`, nestedValue);
    });
    return;
  }

  formData.append(prefix, value);
};

const appendScenarioField = (formData, key, value) => {
  if (value === null || value === undefined) {
    return;
  }

  if (key === "cover") {
    if (value instanceof File) {
      formData.append("scenario[cover]", value);
      return;
    }

    if (value.file instanceof File) {
      formData.append("scenario[cover]", value.file);
      return;
    }

    if (value.path) {
      formData.append("scenario[cover]", JSON.stringify(value));
    }

    return;
  }

  if (key === "document_files") {
    (Array.isArray(value) ? value : []).forEach((item) => {
      const file = item?.file instanceof File ? item.file : item;

      if (file instanceof File) {
        formData.append("scenario[document_files][]", file);
      }
    });
    return;
  }

  if (key === "product_ids") {
    (Array.isArray(value) ? value : []).forEach((id) => {
      formData.append("scenario[product_ids][]", id);
    });
    return;
  }

  if (typeof value === "object") {
    appendNestedFormData(formData, `scenario[${key}]`, value);
    return;
  }

  formData.append(`scenario[${key}]`, value);
};

const scenarioHasUploadFiles = (scenarioData = {}) => {
  const cover = scenarioData.cover;
  const hasCover =
    cover &&
    (cover instanceof File ||
      (cover.file && cover.file instanceof File) ||
      (cover.path && cover.path !== ""));

  const hasDocuments = (Array.isArray(scenarioData.document_files)
    ? scenarioData.document_files
    : []
  ).some((item) => {
    const file = item?.file instanceof File ? item.file : item;

    return file instanceof File;
  });

  return hasCover || hasDocuments;
};

export default class ScenariosService {
  static async getScenarios({page, size, search, sort, with_archived}) {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("page_size", size);
    params.append("with_archived", with_archived);

    if (search) {
      const dateRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4})$/;

      if (dateRegex.test(search.trim())) {
        params.append("q[created_at_gteq]", search.trim());
      } else {
        params.append("q[search_cont]", search);
      }
    }

    if (sort?.value === "products") {
      params.append("q[s][]", `products ${sort.direction || "asc"}`);
    } else if (sort?.value === "status") {
      params.append("q[s][]", `state ${sort.direction || "asc"}`);
    } else if (sort?.value === "created_at") {
      params.append("q[s][]", `created_at ${sort.direction || "asc"}`);
    } else if (sort?.value === "due_date") {
      params.append("q[s][]", `due_date ${sort.direction || "asc"}`);
    } else if (sort?.value) {
      params.append(
        "q[s][]",
        `${sort.value.toLowerCase()} ${sort.direction || "asc"}`,
      );
    }

    return $api.get(`/api/v1/scenarios?${params.toString()}`);
  }

  static async getAllScenarios() {
    return $api.get(`/api/v1/scenarios/simulations_allowed`);
  }

  static async getProducts() {
    return $api.get(`/api/v1/products/scenarios_allowed`);
  }

  static async viewScenario({id}) {
    return $api.get(`/api/v1/scenarios/${id}`);
  }

  static async createScenario({scenario}) {
    if (scenarioHasUploadFiles(scenario.scenario)) {
      const formData = new FormData();

      Object.keys(scenario.scenario).forEach((key) => {
        appendScenarioField(formData, key, scenario.scenario[key]);
      });

      return $api.post(`/api/v1/scenarios`, formData);
    }

    return $api.post(`/api/v1/scenarios`, scenario);
  }

  static async updateScenario({id, scenario}) {
    if (scenarioHasUploadFiles(scenario.scenario)) {
      const formData = new FormData();

      Object.keys(scenario.scenario).forEach((key) => {
        appendScenarioField(formData, key, scenario.scenario[key]);
      });

      return $api.patch(`/api/v1/scenarios/${id}`, formData);
    }

    return $api.patch(`/api/v1/scenarios/${id}`, scenario);
  }

  static async archiveScenario({id}) {
    return $api.post(`/api/v1/scenarios/${id}/archive`);
  }

  static async unArchiveScenario({id}) {
    return $api.post(`/api/v1/scenarios/${id}/restore`);
  }

  static async deleteScenario({id}) {
    return $api.delete(`/api/v1/scenarios/${id}`);
  }

  static async getScenariosFeedbackTypes() {
    return $api.get(`/api/v1/scenarios/feedback_types`);
  }

  static async getCertificationTypes() {
    return $api.get(`/api/v1/certifications/types`);
  }
}
